import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import type { BuildingDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ContextMenu, type ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { CollectionLoadingState } from "@/design-system/CollectionState";
import { Stack } from "@/design-system/Stack";
import { createDashboardColumnHelper, TABLE_COLUMN_SIZES } from "@/design-system/Table/helpers";
import { Table } from "@/design-system/Table/Table";
import { canManageBuildings } from "@/helpers/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { usePermission } from "@/hooks/usePermission";
import { TABLE_KEYS, useResizableTable } from "@/hooks/useResizableTable";
import { useSlug } from "@/hooks/useSlug";
import { buildingMutations } from "@/queries/buildings/mutations";
import { useBuildingQueries } from "@/queries/buildings/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export function BuildingListPage(): React.ReactNode {
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<BuildingDto>("buildings-delete-modal");
  const slug = useSlug();
  const { t } = useTranslation();
  const hasPermission = usePermission();
  const sessionUser = useSessionUser();

  const buildingQueries = useBuildingQueries();
  const {
    data: buildingsData,
    isPending: isPendingBuildingData,
    isFetchingNextPage: isFetchingMoreBuildings,
    error: errorBuildingsData,
    fetchNextPage: fetchMoreBuildings,
    hasNextPage: hasMoreBuildings,
  } = useInfiniteQuery(buildingQueries.listInfinite());
  const deleteBuildingMutation = buildingMutations.useDeleteBuilding();

  const refLoader = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreBuildings && hasMoreBuildings) {
        void fetchMoreBuildings();
      }
    }, [isFetchingMoreBuildings, hasMoreBuildings, fetchMoreBuildings]),
  });

  const buildings = useMemo(() => buildingsData?.pages.flatMap((page) => page.items) || [], [buildingsData]);
  const columns = useMemo(() => {
    const helper = createDashboardColumnHelper<BuildingDto>();

    return [
      helper.accessor("name", {
        header: t("page.buildings.list.name"),
        cell: (cell) => cell.getValue(),
        meta: {
          shouldSimulateRowClick: true,
          renderTruncatedTooltip: (cell) => cell.getValue(),
        },
        minSize: TABLE_COLUMN_SIZES.LEADING_CONTENT.MIN,
        size: TABLE_COLUMN_SIZES.LEADING_CONTENT.DEFAULT,
      }),
      helper.accessor("addressCount", {
        header: t("page.buildings.list.address-count"),
        cell: (cell) => t("page.buildings.list.address-item", { count: cell.getValue() }),
        minSize: TABLE_COLUMN_SIZES.COUNT.MIN,
        size: TABLE_COLUMN_SIZES.COUNT.DEFAULT,
      }),
      helper.accessor("id", {
        header: "",
        cell: (cell) => {
          const menuActions: ContextMenuItem[] = [
            {
              labels: {
                default: t("common.action.edit"),
              },
              value: "edit",
              icon: <Edit05Icon />,
              type: "link",
              href: routes.buildings.edit({ slug, id: cell.getValue() }),
              "data-testid": "context-menu-edit-btn",
              isHidden: !hasPermission(canManageBuildings),
            },
            {
              labels: {
                default: t("common.action.delete"),
              },
              value: "delete",
              icon: <Trash02Icon />,
              type: "button",
              callbackFn: () => openDeleteModal(cell.row.original),
              "data-testid": "context-menu-delete-btn",
              isHidden: !hasPermission(canManageBuildings) || cell.row.original.addressCount > 0,
            },
          ];

          return <ContextMenu items={menuActions} />;
        },
        size: TABLE_COLUMN_SIZES.ACTION.DEFAULT,
        enableResizing: false,
        meta: {
          isAction: true,
        },
      }),
    ];
  }, [t, hasPermission, slug, openDeleteModal]);

  const amountTotalBuildings = useMemo(() => buildingsData?.pages[0]?.total ?? 0, [buildingsData]);
  const tableInstance = useResizableTable(TABLE_KEYS.BUILDINGS, {
    columns,
    data: buildings,
    enableSorting: false,
  });

  if (errorBuildingsData) {
    return <ErrorPage error={errorBuildingsData} />;
  }

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.buildings.title")}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: sessionUser.project.name,
            },
            {
              name: t("page.buildings.title"),
            },
          ]}
        />
      }
      actions={
        hasPermission(canManageBuildings) && (
          <Button type="link" href={routes.buildings.new({ slug })} data-testid="buildings-create-link">
            {t("page.buildings.actions.create")}
          </Button>
        )
      }
    >
      <DeleteModal
        title={t("page.buildings.actions.delete.title")}
        description={t("page.buildings.actions.delete.description")}
        onDelete={(building) => deleteBuildingMutation.mutateAsync(building)}
        deleteBtnProps={{
          "data-testid": "modal-confirm-delete",
        }}
        {...deleteModalProps}
      />
      <Stack gap="sm">
        <span className="text-caption">{`${amountTotalBuildings} ${t("common.entity.building", { count: amountTotalBuildings }).toLowerCase()}`}</span>
        {(buildings.length > 0 || isPendingBuildingData) && (
          <Table<BuildingDto>
            table={tableInstance}
            getRowHref={(row) =>
              routes.buildings.edit({
                slug,
                id: row.original.id,
              })
            }
            readOnly={!hasPermission(canManageBuildings)}
            data-testid="buildings-list"
            isLoading={isPendingBuildingData}
          />
        )}
        {hasMoreBuildings && <CollectionLoadingState ref={refLoader} />}
      </Stack>
    </DocumentPage>
  );
}
